import api from "./client";
import axios from "axios";


// ---------- TIPOS ----------
export interface Armazem {
	armazem_id: number;
	nome: string;
	endereco: string;
	cidade: string;
	estado: string;
	altura?: number;
	largura?: number;
	comprimento?: number;
	is_principal?: boolean;
	is_full_master?: boolean;
	is_extensao_principal?: boolean;
	is_excluido_reserva?: boolean;
}


// Função otimizada para buscar apenas quantidades das localizações
// NOTA: Endpoint /localizacao/quantidades não existe no backend
// As quantidades já vêm na busca principal de localizações
export async function buscarQuantidadesLocalizacoes(): Promise<
	Record<number, number>
> {
	// Retorna objeto vazio - as quantidades devem vir da API principal
	return {} as Record<number, number>;
}


export async function buscarProdutosPorLocalizacao(localizacao_id: number) {
	try {
		// Buscar produtos APENAS da localização específica
		// Não carregar todos os produtos de todas as localizações
		const res = await api.get(`/produto-estoque/localizacao/${localizacao_id}`);

		if (res.data && Array.isArray(res.data)) {
			return res.data
				.filter(
					(item: any) =>
						(item.quantidade || 0) > 0 || (item.estoque_reservado || 0) > 0,
				)
				.map((item: any) => ({
					produto_estoque_id:
						item.produto_estoque_id ?? item.id ?? item.produtoEstoqueId ?? null,
					produto_id: item.produto?.produto_id ?? null,
					descricao: item.produto?.descricao || item.descricao || "",
					sku: item.produto?.sku || item.sku || "",
					ean: item.produto?.ean || item.ean || "",
					quantidade: item.quantidade || 0,
					estoque_reservado: item.estoque_reservado || 0,
				}));
		}

		return [];
	} catch (err) {
		console.error("Erro ao buscar produtos da localização:", err);
		// Fallback: se a API específica não existir, usar a função antiga
		try {
			const todos = await buscarConsultaEstoque();
			return todos
				.filter(
					(item: any) =>
						item.localizacao_id === localizacao_id &&
						(item.quantidade || 0) > 0,
				) // Filtrar por localização E quantidade > 0
				.map((item: any) => ({
					produto_estoque_id:
						item.produto_estoque_id ?? item.id ?? item.produtoEstoqueId ?? null,
					produto_id: item.produto?.produto_id ?? null,
					descricao: item.descricao || "",
					sku: item.sku || "",
					ean: item.ean || "",
					quantidade: item.quantidade || 0,
					estoque_reservado: item.estoque_reservado || 0,
				}));
		} catch (fallbackErr) {
			console.error("Erro no fallback:", fallbackErr);
			throw new Error("Falha ao carregar produtos da localização.");
		}
	}
}


export async function buscarReservasPorProdutoEstoque(
	produto_estoque_id: number,
) {
	try {
		const res = await api.get(
			`/produto-estoque/${produto_estoque_id}/reservas`,
		);
		return res.data;
	} catch (err) {
		console.error("Erro ao buscar reservas do produto_estoque:", err);
		throw err;
	}
}


// ---------- POST /armazem ----------
export interface CriarArmazemPayload {
	nome: string;
	endereco: string;
	cidade: string;
	estado: string; // ex.: "SP"
	largura?: number; // cm
	altura?: number; // cm
	comprimento?: number; // cm
}

export const atualizarArmazem = async (
	id: number,
	dados: {
		nome: string;
		endereco: string;
		largura?: number;
		altura?: number;
		comprimento?: number;
	},
) => {
	try {
		await api.patch(`/armazem/${id}`, dados);
	} catch (err: any) {
		console.error("Erro ao atualizar armazém:", err);
		throw new Error("Falha ao atualizar armazém.");
	}
};


/**
 * Cria um novo armazém.
 * Converte dimensões para número e lança erros detalhados.
 */
export const criarArmazem = async (
	dados: CriarArmazemPayload,
): Promise<Armazem> => {
	try {
		// Garantir que dimensões sejam numéricas ou undefined
		const payload = {
			...dados,
			largura: dados.largura ? Number(dados.largura) : undefined,
			altura: dados.altura ? Number(dados.altura) : undefined,
			comprimento: dados.comprimento ? Number(dados.comprimento) : undefined,
		};

		const { data } = await api.post<Armazem>("/armazem", payload);
		return data;
	} catch (err: any) {
		console.error("Erro ao criar armazém →", err);

		if (axios.isAxiosError(err) && err.response) {
			throw new Error(
				`Erro ${err.response.status}: ${JSON.stringify(err.response.data)}`,
			);
		}
		throw new Error("Falha ao criar o armazém no servidor.");
	}
};


export interface Localizacao {
	localizacao_id: number;
	status: "aberta" | "fechada";
	nome: string;
	tipo: string;
	tipo_localizacao_id: number;
	armazem: string;
	armazem_id: number;
	ean: string;
	total_produtos: number;
	estoque_reservado: number;
}


export const buscarLocalizacoes = async (
	limit: number = 10000,
	offset: number = 0,
	busca: string = "",
	armazemId?: number,
	tipoId?: number,
	status?: "aberta" | "fechada",
): Promise<{ results: Localizacao[]; total: any }> => {
	try {
		const params = new URLSearchParams();

		params.set("limit", String(limit));
		params.set("offset", String(offset));

		if (busca?.trim()) {
			params.set("search", busca.trim());
		}
		if (typeof armazemId === "number") {
			params.set("armazemId", armazemId.toString());
		}
		if (typeof tipoId === "number") {
			params.set("tipoId", tipoId.toString());
		}
		if (status) {
			params.set("status", status);
		}

		const res = await api.get<{ results: any[]; total: any }>(
			`/localizacao?${params.toString()}`,
		);

		const dados: Localizacao[] = res.data.results.map((item: { localizacao_id: any; status: any; nome: any; tipo: { tipo_localizacao_id: any; tipo: any; }; armazem: { armazem_id: any; nome: any; }; ean: any; total_produtos: any; estoque_reservado: any; }) => ({
			localizacao_id: item.localizacao_id,
			status: item.status,
			nome: item.nome,
			tipo_localizacao_id: Number(item.tipo?.tipo_localizacao_id) || 0,
			tipo: item.tipo?.tipo ?? "",
			armazem_id: Number(item.armazem?.armazem_id) || 0,
			armazem: item.armazem?.nome ?? "",
			ean: item.ean ?? "",
			total_produtos: item.total_produtos ?? 0,
			estoque_reservado: item.estoque_reservado ?? 0,
		}));

		return {
			results: dados,
			total: res.data.total.total_itens,
		};
	} catch (err) {
		console.error("Erro ao buscar localizações →", err);
		throw new Error("Falha ao carregar as localizações do servidor.");
	}
};


export const fecharLocalizacaoAdmin = async (
	localizacao_id: number,
): Promise<void> => {
	try {
		await api.patch(`/localizacao/${localizacao_id}/fechar`);
	} catch (err) {
		console.error("Erro ao fechar localização →", err);
		throw new Error("Falha ao fechar a localização.");
	}
};


export interface ZerarQuantidadeResult {
	ok: true;
	produtos_estoque_atualizados: number;
	ja_estava_zerada?: boolean;
}

export const zerarQuantidadeLocalizacaoAdmin = async (
	localizacao_id: number,
): Promise<ZerarQuantidadeResult> => {
	try {
		const { data } = await api.post<ZerarQuantidadeResult>(
			`/localizacao/${localizacao_id}/zerar-quantidade`,
		);
		return data;
	} catch (err) {
		console.error("Erro ao zerar quantidade da localização →", err);
		throw new Error("Falha ao zerar a quantidade da localização.");
	}
};

export interface ZerarQuantidadeLoteResult {
	total: number;
	sucessos: number[];
	ignorados: number[];
	falhas: { localizacao_id: number; motivo: string }[];
}

export const zerarQuantidadeLocalizacoesLoteAdmin = async (
	localizacao_ids: number[],
): Promise<ZerarQuantidadeLoteResult> => {
	try {
		const { data } = await api.post<ZerarQuantidadeLoteResult>(
			"/localizacao/zerar-quantidade-lote",
			{ localizacao_ids },
		);
		return data;
	} catch (err) {
		console.error("Erro ao zerar quantidade em lote →", err);
		throw new Error("Falha ao zerar a quantidade em lote.");
	}
};


export const excluirTipoLocalizacao = async (id: number): Promise<void> => {
	try {
		await api.delete(`/tipo-localizacao/${id}`);
	} catch (err) {
		console.error("Erro ao excluir tipo de localização:", err);
		throw new Error("Falha ao excluir o tipo de localização.");
	}
};


export const buscarTipoLocalizacao = async (
	id: number,
): Promise<TipoLocalizacao> => {
	try {
		const { data } = await api.get(`/tipo-localizacao/${id}`);
		return data;
	} catch (err) {
		console.error("Erro ao buscar tipo de localização:", err);
		throw new Error("Falha ao carregar o tipo de localização.");
	}
};

export const atualizarTipoLocalizacao = async (
	id: number,
	payload: { tipo: string },
): Promise<void> => {
	try {
		await api.patch(`/tipo-localizacao/${id}`, payload);
	} catch (err) {
		console.error("Erro ao atualizar tipo de localização:", err);
		throw new Error("Falha ao atualizar tipo de localização.");
	}
};

export const criarTipoLocalizacao = async (payload: {
	tipo: string;
}): Promise<void> => {
	try {
		await api.post(`/tipo-localizacao`, payload);
	} catch (err) {
		console.error("Erro ao criar tipo de localização:", err);
		throw new Error("Falha ao criar tipo de localização.");
	}
};


export interface Marca {
	marca_id: number;
	nome: string;
	descricao?: string | null;
	id_tiny?: number | null;
}


export interface Categoria {
	categoria_id: number;
	nome: string;
	id_tiny?: number | null;
	caminho_completo?: string | null;
}


export interface Produto {
	produto_id: number;
	id_tiny?: number;
	url_foto: string;
	descricao: string;
	sku: string;
	ean: string;
	altura?: number;
	largura?: number;
	comprimento?: number;
	quantidade?: number;
	estoque_reservado?: number;
	quantidade_ocorrencia?: number;
	marca?: {
		marca_id: number;
		nome: string;
		descricao?: string | null;
		id_tiny?: number | null;
	} | null;
	categoria?: {
		categoria_id: number;
		nome: string;
		id_tiny?: number | null;
		caminho_completo?: string | null;
	} | null;
}


export const buscarMarcas = async (): Promise<Marca[]> => {
	try {
		const response = await api.get<Marca[]>("/marca");
		return response.data;
	} catch (error) {
		console.error("Erro ao buscar marcas:", error);
		throw error;
	}
};


export const buscarCategorias = async (): Promise<Categoria[]> => {
	try {
		const response = await api.get<Categoria[]>("/categoria");
		return response.data;
	} catch (error) {
		console.error("Erro ao buscar categorias:", error);
		throw error;
	}
};


export const criarCategoria = async (dados: {
	nome: string;
	id_tiny?: number;
	caminho_completo?: string;
}): Promise<Categoria> => {
	try {
		const response = await api.post<Categoria>("/categoria", dados);
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao criar categoria:",
			error.response?.data || error.message,
		);
		throw error;
	}
};


export const atualizarCategoria = async (
	id: number,
	dados: {
		nome?: string;
		id_tiny?: number;
		caminho_completo?: string;
	},
): Promise<Categoria> => {
	try {
		const response = await api.patch<Categoria>(`/categoria/${id}`, dados);
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao atualizar categoria:",
			error.response?.data || error.message,
		);
		throw error;
	}
};


export const deletarCategoria = async (id: number): Promise<void> => {
	try {
		await api.delete(`/categoria/${id}`);
	} catch (error: any) {
		console.error(
			"Erro ao deletar categoria:",
			error.response?.data || error.message,
		);
		throw error;
	}
};


export const buscarMarcaPorId = async (id: number): Promise<Marca> => {
	try {
		const response = await api.get<Marca>(`/marca/${id}`);
		return response.data;
	} catch (error) {
		console.error("Erro ao buscar marca:", error);
		throw error;
	}
};


export const criarMarca = async (dados: {
	nome: string;
	descricao?: string;
	id_tiny?: number;
}): Promise<Marca> => {
	try {
		const response = await api.post<Marca>("/marca", dados);
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao criar marca:",
			error.response?.data || error.message,
		);
		throw error;
	}
};


export const atualizarMarca = async (
	id: number,
	dados: {
		nome?: string;
		descricao?: string;
		id_tiny?: number;
	},
): Promise<Marca> => {
	try {
		const response = await api.patch<Marca>(`/marca/${id}`, dados);
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao atualizar marca:",
			error.response?.data || error.message,
		);
		throw error;
	}
};


export const deletarMarca = async (id: number): Promise<void> => {
	try {
		await api.delete(`/marca/${id}`);
	} catch (error: any) {
		console.error(
			"Erro ao deletar marca:",
			error.response?.data || error.message,
		);
		throw error;
	}
};


export const buscarProdutos = async (
	limit: number = 50,
	offset: number = 0,
	busca: string = "",
	quantidadeMin?: number,
	quantidadeMax?: number,
	sortField?: string,
	sortDirection?: string,
	marcaId?: number,
	categoriaId?: number,
): Promise<{ results: Produto[]; total: number }> => {
	try {
		const params = new URLSearchParams();

		params.set("limit", String(limit));
		params.set("offset", String(offset));

		if (busca?.trim()) {
			params.set("search", busca.trim());
		}

		if (quantidadeMin !== undefined && quantidadeMin !== null) {
			params.set("quantidade_min", String(quantidadeMin));
		}

		if (quantidadeMax !== undefined && quantidadeMax !== null) {
			params.set("quantidade_max", String(quantidadeMax));
		}

		if (sortField) {
			params.set("sort_field", sortField);
		}

		if (sortDirection) {
			params.set("sort_direction", sortDirection);
		}

		if (marcaId !== undefined && marcaId !== null) {
			params.set("marca_id", String(marcaId));
		}

		if (categoriaId !== undefined && categoriaId !== null) {
			params.set("categoria_id", String(categoriaId));
		}

		const res = await api.get<{ results: any[]; total: number }>(
			`/produto?${params.toString()}`,
		);

		const dados: Produto[] = res.data.results.map((item) => ({
			produto_id: item.produto_id,
			id_tiny: item.id_tiny,
			url_foto: item.url_foto ?? "",
			descricao: item.descricao,
			sku: item.sku,
			ean: item.ean ?? "",
			altura: item.altura,
			largura: item.largura,
			comprimento: item.comprimento,
			quantidade: item.quantidade_total || item.quantidade || 0,
			estoque_reservado:
				item.estoque_reservado_total || item.estoque_reservado || 0,
			quantidade_ocorrencia:
				item.quantidade_ocorrencia || item.quantidade_ocorrencia_total || 0,
			marca: item.marca || null,
			categoria: item.categoria || null,
		}));

		return {
			results: dados,
			total: res.data.total || dados.length,
		};
	} catch (err) {
		console.error("Erro ao buscar produtos →", err);
		throw new Error("Falha ao carregar os produtos do servidor.");
	}
};


// Sincroniza um único produto com a Tiny: corrige o id_tiny e atualiza
// descrição/foto a partir da Tiny. Não faz try/catch de propósito — o erro do
// axios propaga com a mensagem do backend (produto não encontrado na Tiny,
// id_tiny em conflito), pra ser exibida no toast pela camada de UI.
export async function sincronizarProdutoComTiny(
  produtoId: number,
): Promise<Produto> {
  const res = await api.patch<Produto>(
    `/produto/${produtoId}/sincronizar-tiny`,
  );
  return res.data;
}

export async function buscarEstoqueBatch(
  codigos: string[],
): Promise<Record<string, { quantidade: number; estoque_reservado: number; quantidade_ocorrencia: number; disponivel: number }>> {
  try {
    const res = await api.post("/produto/estoque-batch", { codigos });
    return res.data || {};
  } catch (err) {
    console.error("Erro ao buscar estoque batch:", err);
    return {};
  }
}

export async function buscarConsultaEstoque() {
	try {
		const todos: any[] = [];
		const limite = 10000;
		let offset = 0;
		let total = Infinity;

		while (todos.length < total) {
			const res = await api.get("/produto-estoque", {
				params: { limit: limite, offset: offset },
			});

			const results = res.data?.results || res.data;
			const formatados = results.map((item: any) => ({
				produto_estoque_id: item.produto_estoque_id ?? null,
				produto_id:
					item.produto?.produto_id ??
					`sem-id-${item.produto?.sku}-${item.localizacao?.localizacao_id}`,
				descricao: item.produto?.descricao || "",
				sku: item.produto?.sku || "",
				ean: item.produto?.ean || "",
				quantidade: item.quantidade || 0,
				localizacao_id: item.localizacao?.localizacao_id ?? null,
				localizacao: item.localizacao?.nome || "",
				localizacao_ean: item.localizacao?.ean,
				armazem: item.localizacao?.armazem?.nome || "",
				armazem_id: item.localizacao?.armazem?.armazem_id ?? null,
				tipo_localizacao: item.localizacao?.tipo?.tipo || "",
				tipo_localizacao_id:
					item.localizacao?.tipo?.tipo_localizacao_id ?? null,
			}));

			todos.push(...formatados);
			total = res.data?.total ?? results.length;
			offset += limite;
		}

		return todos;
	} catch (err) {
		console.error("Erro ao buscar consulta de estoque →", err);
		throw new Error("Falha ao carregar os dados de estoque.");
	}
}


export async function buscarConsultaEstoquePesquisa(
	search?: string,
	offset: number = 0,
	limit: number = 50,
	tipoId?: number,
	armazemId?: number,
	quantidadeMin?: number,
	quantidadeMax?: number,
	signal?: AbortSignal,
) {
	try {
		const params: any = {
			offset,
			limit,
		};

		if (search) {
			params.search = search;
		}

		if (tipoId) {
			params.tipoId = tipoId;
		}

		if (armazemId) {
			params.armazemId = armazemId;
		}

		if (quantidadeMin !== undefined && quantidadeMin !== null) {
			params.quantidadeMin = quantidadeMin;
		}

		if (quantidadeMax !== undefined && quantidadeMax !== null) {
			params.quantidadeMax = quantidadeMax;
		}

		const res = await api.get("/produto-estoque/pesquisar", {
			params,
			...(signal && { signal }),
		});

		const results = res.data?.results || [];
		const total = res.data?.total || 0;

		const formatados = results.map((item: any) => ({
			produto_estoque_id: item.produto_estoque_id ?? null,
			produto_id:
				item.produto?.produto_id ??
				`sem-id-${item.produto?.sku}-${item.localizacao?.localizacao_id}`,
			descricao: item.produto?.descricao || "",
			sku: item.produto?.sku || "",
			ean: item.produto?.ean || "",
			url_foto: item.produto?.url_foto || "",
			quantidade: item.quantidade || 0,
			estoque_reservado: item.estoque_reservado || 0,
			localizacao_id: item.localizacao?.localizacao_id ?? null,
			localizacao: item.localizacao?.nome || "",
			localizacao_ean: item.localizacao?.ean,
			armazem: item.localizacao?.armazem?.nome || "",
			armazem_id: item.localizacao?.armazem?.armazem_id ?? null,
			tipo_localizacao: item.localizacao?.tipo?.tipo || "",
			tipo_localizacao_id: item.localizacao?.tipo?.tipo_localizacao_id ?? null,
			tem_ocorrencia: !!item.tem_ocorrencia_ativa,
		}));

		return { results: formatados, total };
	} catch (err) {
		console.error("Erro ao buscar consulta de estoque com pesquisa →", err);
		throw new Error("Falha ao carregar os dados de estoque.");
	}
}


// Novo: busca uma localização individual
export const buscarLocalizacao = async (id: number) => {
	const resp = await api.get(`/localizacao/${id}`);
	return resp.data; // { nome, tipo, altura, largura, comprimento, … }
};


// Hidrata um conjunto conhecido de localizações num ÚNICO request (?ids=1,2,3),
// evitando N chamadas a /localizacao/:id que estouram o ThrottlerGuard (429).
// Reaproveita o mesmo mapeamento de `buscarLocalizacoes`.
export const buscarLocalizacoesPorIds = async (
	ids: number[],
): Promise<Localizacao[]> => {
	const idsValidos = Array.from(
		new Set(ids.filter((n) => Number.isFinite(n) && n > 0)),
	);
	if (idsValidos.length === 0) return [];

	try {
		const params = new URLSearchParams();
		params.set("ids", idsValidos.join(","));
		params.set("limit", String(idsValidos.length));

		const res = await api.get<{ results: any[]; total: any }>(
			`/localizacao?${params.toString()}`,
		);

		return res.data.results.map((item) => ({
			localizacao_id: item.localizacao_id,
			status: item.status,
			nome: item.nome,
			tipo_localizacao_id: Number(item.tipo?.tipo_localizacao_id) || 0,
			tipo: item.tipo?.tipo ?? "",
			armazem_id: Number(item.armazem?.armazem_id) || 0,
			armazem: item.armazem?.nome ?? "",
			ean: item.ean ?? "",
			total_produtos: item.total_produtos ?? 0,
			estoque_reservado: item.estoque_reservado ?? 0,
		}));
	} catch (err) {
		console.error("Erro ao buscar localizações por IDs →", err);
		throw new Error("Falha ao carregar as localizações do servidor.");
	}
};


// Novo: atualiza localização
// services/API.ts
export const atualizarLocalizacao = async (id: number, payload: any) => {
	await api.patch(`/localizacao/${id}`, payload);
};


export interface Armazem {
	armazem_id: number;
	nome: string;
	endereco: string;
}


export const buscarArmazem = async (): Promise<Armazem[]> => {
	try {
		const res = await api.get<Armazem[]>("/armazem");
		return res.data;
	} catch (err) {
		console.error("Erro ao buscar armazéns →", err);
		throw new Error("Falha ao carregar os armazéns do servidor.");
	}
};


export interface TipoLocalizacao {
	tipo_localizacao_id: number;
	tipo: string;
}


export const buscarTiposDeLocalizacao = async (): Promise<
	TipoLocalizacao[]
> => {
	try {
		const res = await api.get<TipoLocalizacao[]>("/tipo-localizacao");

		return res.data;
	} catch (err) {
		console.error("Erro ao buscar tipos de localização →", err);
		throw new Error("Falha ao carregar os tipos de localização do servidor.");
	}
};


// Função para buscar filtros disponíveis para localizações
export const buscarFiltrosLocalizacao = async (): Promise<{
	armazens: { armazem_id: number; nome: string }[];
	tipos: { tipo_localizacao_id: number; tipo: string }[];
}> => {
	try {
		const [armazensRes, tiposRes] = await Promise.all([
			api.get<Armazem[]>("/armazem"),
			api.get<TipoLocalizacao[]>("/tipo-localizacao"),
		]);

		const result = {
			armazens: armazensRes.data.map((a) => ({
				armazem_id: a.armazem_id,
				nome: a.nome,
			})),
			tipos: tiposRes.data.map((t) => ({
				tipo_localizacao_id: t.tipo_localizacao_id,
				tipo: t.tipo,
			})),
		};
		return result;
	} catch (err) {
		console.error("Erro ao buscar filtros de localização →", err);
		throw new Error("Falha ao buscar filtros de localização.");
	}
};


// Função para buscar filtros disponíveis para pedidos
export const buscarFiltrosPedidos = async (): Promise<{
	canaisVenda: { canal_venda: string }[];
	status: { situacao: number; label: string }[];
	marcadores: { flag_id: number; nome: string }[];
	statusEstoque: { value: string; label: string; total?: number }[];
}> => {
	try {
		const [canaisRes, statusRes, marcadoresRes, statusEstoqueRes] =
			await Promise.all([
				api.get<{ canal_venda: string }[]>(
					"/pedido/filtros/canais-venda",
				),
				api.get<{ situacao: number; label: string }[]>(
					"/pedido/filtros/status",
				),
				api.get<{ flag_id: number; nome: string }[]>(
					"/pedido/filtros/marcadores",
				),
				api.get<{ value: string; label: string; total?: number }[]>(
					"/pedido/filtros/status-estoque",
				),
			]);

		const result = {
			canaisVenda: canaisRes.data,
			status: statusRes.data,
			marcadores: marcadoresRes.data,
			statusEstoque: statusEstoqueRes.data,
		};
		return result;
	} catch (err) {
		console.error("Erro ao buscar filtros de pedidos →", err);
		throw new Error("Falha ao buscar filtros de pedidos.");
	}
};


// Função para buscar filtros disponíveis para trocas
export const buscarFiltrosTrocas = async (): Promise<{
	canaisVenda: { canal_venda: string }[];
}> => {
	try {
		const canaisRes = await api.get<{ canal_venda: string }[]>(
			"/pedido/filtros/canais-venda",
		);

		return { canaisVenda: canaisRes.data };
	} catch (err) {
		console.error("Erro ao buscar filtros de trocas →", err);
		throw new Error("Falha ao buscar filtros de trocas.");
	}
};


export interface CriarLocalizacaoPayload {
	nome: string;
	status: string;
	tipo: string;
	armazem: string;
	altura: string;
	largura: string;
	comprimento: string;
	envio_id?: string;
	vendivel?: boolean;
}


/**
 * Traduz um erro (axios ou não) numa mensagem pronta para exibição ao usuário.
 * A camada de serviço não exibe nada: quem chama decide como mostrar (snackbar).
 */
const descreverErro = (err: unknown, acaoFallback: string): string => {
	if (axios.isAxiosError(err)) {
		if (err.response) {
			const dados = err.response.data as
				| { message?: string | string[] }
				| undefined;
			const mensagemApi = Array.isArray(dados?.message)
				? dados?.message.join(", ")
				: dados?.message;

			return (
				mensagemApi ||
				`Erro ${err.response.status}: ${JSON.stringify(err.response.data)}`
			);
		}

		if (err.request) {
			return "Nenhuma resposta recebida do servidor.";
		}

		return "Erro na configuração da requisição.";
	}

	if (err instanceof Error && err.message) {
		return err.message;
	}

	return acaoFallback;
};


export const criarLocalizacao = async (
	dados: CriarLocalizacaoPayload,
): Promise<void> => {
	try {
		const armazens = await buscarArmazem();
		const tipos = await buscarTiposDeLocalizacao();

		const armazemSelecionado = armazens.find(
			(a) => a.nome.toLowerCase() === dados.armazem.toLowerCase(),
		);

		if (!armazemSelecionado?.armazem_id) {
			throw new Error(
				`Armazém "${
					dados.armazem
				}" não encontrado."${dados.tipo.toLowerCase()}"`,
			);
		}

		const tipoSelecionado = tipos.find(
			(t) => t.tipo.toLowerCase() === dados.tipo.toLowerCase(),
		);

		if (!tipoSelecionado?.tipo_localizacao_id) {
			throw new Error(`Tipo de localização "${dados.tipo}" não encontrado.`);
		}

		await api.post("/localizacao", {
			nome: dados.nome,
			status: "fechada",
			altura: dados.altura,
			largura: dados.largura,
			comprimento: dados.comprimento,
			tipo_localizacao_id: tipoSelecionado.tipo_localizacao_id,
			armazem_id: armazemSelecionado.armazem_id,
			...(dados.envio_id ? { envio_id: Number(dados.envio_id) } : {}),
			...(dados.vendivel !== undefined ? { vendivel: dados.vendivel } : {}),
		});
	} catch (err: unknown) {
		console.error("Erro ao criar nova localização →", err);

		if (axios.isAxiosError(err) && err.response) {
			console.error("❌ Status do erro:", err.response.status);
			console.error(
				"❌ Dados da resposta:",
				JSON.stringify(err.response.data, null, 2),
			);
		}

		throw new Error(descreverErro(err, "Erro inesperado ao criar localização."));
	}
};


export interface ExcluirLocalizacao {
	localizacao_id: number;
}


export const excluirLocalizacao = async ({
	localizacao_id,
}: ExcluirLocalizacao): Promise<void> => {
	try {
		await api.delete(`/localizacao/${localizacao_id}`);
	} catch (err: unknown) {
		console.error("Erro ao excluir localização →", err);

		throw new Error(
			descreverErro(err, "Erro inesperado ao excluir localização."),
		);
	}
};


export async function buscarProdutoPorEAN(
	ean: string,
	eanLocalizacao?: string,
) {
	const eanLimpo = ean.replace(/[\n\r\t\s]/g, "").trim();

	try {
		const response = await api.get(`/produto/buscar-por-ean/${eanLimpo}`);
		const produto = response.data;

		let produto_estoque_id;

		if (eanLocalizacao) {
			try {
				const estoque = await buscarProdutoEstoquePorLocalizacaoEAN(
					eanLocalizacao,
					eanLimpo,
				);
				produto_estoque_id = estoque?.produto_estoque_id;
			} catch {
				produto_estoque_id = undefined;
			}
		}

		return {
			produto_id: produto.produto_id,
			produto_estoque_id,
			sku: produto.sku || "",
			ean: produto.ean || "",
			descricao: produto.descricao || "",
		};
	} catch {
		throw new Error("Produto com esse EAN não encontrado.");
	}
}


export async function buscarLocalizacaoPorEAN(ean: string) {
	try {
		const eanLimpo = ean.replace(/[\n\r\t\s]/g, "").trim();
		const response = await api.get(`/localizacao/buscar-por-ean/${eanLimpo}`);
		const loc = response.data;

		if (!loc || !loc.localizacao_id) {
			throw new Error("Localização com esse EAN não encontrada.");
		}

		return {
			localizacao_id: loc.localizacao_id,
			nome: loc.nome || loc.localizacao_nome || "",
			// Backend pode retornar `armazem_nome` (string) ou `armazem: { nome }`
			armazem: loc?.armazem?.nome || loc.armazem_nome || "",
			ean: loc.ean || eanLimpo,
			// Backend pode retornar `tipo: { tipo }`
			tipo: loc?.tipo?.tipo || loc.tipo || "",
		};
	} catch (err) {
		console.error("Erro ao buscar localização:", err);
		throw new Error("Erro ao buscar localização.");
	}
}


// ---------- SOLICITAÇÃO DE AJUSTE (CONFERÊNCIA LOCALIZAÇÃO) ----------
export type StatusSolicitacaoAjuste =
	| "pendente"
	| "processamento"
	| "executada"
	| "rejeitada";


export interface SolicitacaoAjusteItemDTO {
	produto_id: number;
	produto_estoque_id?: number | null;
	sku?: string | null;
	descricao?: string | null;
	quantidade_sistema: number;
	quantidade_fisica: number;
	diferenca: number;
}


export interface SolicitacaoAjusteDTO {
	solicitacao_ajuste_id: number;
	localizacao_nome?: string | null;
	solicitante_usuario?: {
		usuario_id: number;
		usuario: string;
		responsavel: string;
	} | null;
	aprovador_usuario?: {
		usuario_id: number;
		usuario: string;
		responsavel: string;
	} | null;
	rejeitador_usuario?: {
		usuario_id: number;
		usuario: string;
		responsavel: string;
	} | null;
	localizacao?: {
		localizacao_id: number;
		nome?: string;
		ean?: string;
	} | null;
	tipo: string;
	observacoes?: string | null;
	status: StatusSolicitacaoAjuste;
	data_criacao: string;
	data_aprovacao?: string | null;
	data_rejeicao?: string | null;
	itens: SolicitacaoAjusteItemDTO[];
}


export async function listarSolicitacoesAjuste(params?: {
	status?: StatusSolicitacaoAjuste;
}): Promise<SolicitacaoAjusteDTO[]> {
	const { data } = await api.get("/solicitacao-ajuste", { params });
	return data;
}


export async function criarSolicitacaoAjuste(payload: {
	localizacao_id?: number;
	localizacao_nome?: string;
	tipo?: string;
	observacoes?: string;
	itens: SolicitacaoAjusteItemDTO[];
}): Promise<SolicitacaoAjusteDTO> {
	const { data } = await api.post("/solicitacao-ajuste", payload);
	return data;
}


export async function executarSolicitacaoAjuste(
	solicitacao_ajuste_id: number,
): Promise<SolicitacaoAjusteDTO> {
	const { data } = await api.post(
		`/solicitacao-ajuste/${solicitacao_ajuste_id}/executar`,
	);
	return data;
}


export async function rejeitarSolicitacaoAjuste(
	solicitacao_ajuste_id: number,
	motivo?: string,
): Promise<SolicitacaoAjusteDTO> {
	const { data } = await api.post(
		`/solicitacao-ajuste/${solicitacao_ajuste_id}/rejeitar`,
		motivo ? { motivo } : {},
	);
	return data;
}

// ---------- DETALHE DA SINCRONIZAÇÃO COM A TINY (por produto) ----------
export type TinyItemStatus =
	| "pendente"
	| "aplicado"
	| "sem_acao"
	| "pulado_reserva"
	| "supervisor"
	| "sem_id_tiny"
	| "erro";

export interface SolicitacaoTinyItemDTO {
	produto_id: number;
	sku?: string | null;
	descricao?: string | null;
	tiny_status: TinyItemStatus | null;
	tiny_motivo: string | null;
	tiny_alvo: number | null;
	tiny_saldo_antes: string | null;
	tiny_saldo_depois: string | null;
	tiny_delta: number | null;
	tiny_reserva_wms: number | null;
	tiny_reserva_tiny: string | null;
	tiny_id_lancamento: string | null;
	tiny_erro: string | null;
	tiny_processado_em: string | null;
}

export interface SolicitacaoTinyDetalheDTO {
	solicitacao_ajuste_id: number;
	status: StatusSolicitacaoAjuste;
	solicitante: string | null;
	aprovador: string | null;
	resumo: Record<string, number>;
	itens: SolicitacaoTinyItemDTO[];
}

export async function obterDetalheTinySolicitacao(
	solicitacao_ajuste_id: number,
): Promise<SolicitacaoTinyDetalheDTO> {
	const { data } = await api.get(
		`/solicitacao-ajuste/${solicitacao_ajuste_id}/tiny`,
	);
	return data;
}

/**
 * Reprocessa a sincronização Tiny (ação do supervisor).
 * - forcar=false: tenta de novo os itens em erro.
 * - forcar=true: aplica os itens em supervisor ignorando o gap.
 * Devolve o detalhe atualizado.
 */
export async function reprocessarTinySolicitacao(
	solicitacao_ajuste_id: number,
	opts: { forcar?: boolean; produto_ids?: number[] },
): Promise<SolicitacaoTinyDetalheDTO> {
	const { data } = await api.post(
		`/solicitacao-ajuste/${solicitacao_ajuste_id}/tiny/reprocessar`,
		opts,
	);
	return data;
}


export interface ProdutoEstoqueDTO {
	produto_estoque_id: number;
	produto_id: number;
	descricao: string;
	sku: string;
	ean: string;
	quantidade: number;
	estoque_reservado: number;
}


export async function buscarProdutosPorLocalizacaoDireto(
	localizacao_id: number,
): Promise<ProdutoEstoqueDTO[]> {
	try {
		// ✅ USAR O ENDPOINT CORRETO DE LOCALIZAÇÃO
		const res = await api.get(`/localizacao/${localizacao_id}/produtos`);

		const dados = res.data?.produtos_estoque || [];

		const resultado = dados
			.filter(
				(item: any) =>
					(item.quantidade || 0) > 0 || (item.estoque_reservado || 0) > 0,
			)
			.map((item: any) => ({
				produto_estoque_id: item.produto_estoque_id,
				produto_id: item.produto?.produto_id,
				descricao: item.produto?.descricao || "",
				sku: item.produto?.sku || "",
				ean: item.produto?.ean || "",
				quantidade: item.quantidade || 0,
				estoque_reservado: item.estoque_reservado || 0,
			}));

		return resultado;
	} catch (error) {
		throw new Error("Erro ao buscar produtos da localização: " + error);
	}
}


export async function buscarProdutoEstoquePorId(id: number) {
	try {
		const res = await api.get(`/produto-estoque/${id}`);
		return res.data;
	} catch (err: any) {
		console.error(`❌ Erro ao buscar produto_estoque ID ${id}:`, err);
		return null;
	}
}


export async function buscarLocalizacaoGeral(ean: string) {
	const response = await api.get("/localizacao");
	const localizacoes = response.data.results;

	const encontrada = localizacoes.find((l: any) => l.ean === ean.trim());

	if (!encontrada) {
		throw new Error("Localização com esse EAN não encontrada.");
	}

	return encontrada;
}


const cacheProdutosPorLocalizacao = new Map<number, ProdutoEstoqueDTO[]>();

export async function buscarProdutoEstoquePorLocalizacaoEAN(
	eanLocalizacao: string,
	eanProduto: string,
) {
	try {
		const eanLoc = eanLocalizacao.replace(/[\n\r\t\s]/g, "").trim();
		const codProd = eanProduto.replace(/[\n\r\t\s]/g, "").trim();

		const localizacao = await buscarLocalizacaoPorEAN(eanLoc);
		const localizacaoID = localizacao.localizacao_id;

		let produtos = cacheProdutosPorLocalizacao.get(localizacaoID);

		if (!produtos) {
			produtos = await buscarProdutosPorLocalizacaoDireto(localizacaoID);
			cacheProdutosPorLocalizacao.set(localizacaoID, produtos);
		}

		const encontrado = produtos.find((p) => {
			const eanLimpo = p.ean?.replace(/[\n\r\t\s]/g, "").trim();
			const skuLimpo = p.sku?.replace(/[\n\r\t\s]/g, "").trim();

			return p && (eanLimpo === codProd || skuLimpo === codProd);
		});

		if (!encontrado) {
			throw new Error("Produto não encontrado nesta localização.");
		}

		return {
			produto_estoque_id: encontrado.produto_estoque_id,
			localizacao_id: localizacaoID,
			quantidade: encontrado.quantidade || 0,
		};
	} catch (err: any) {
		console.error("Erro em buscarProdutoEstoquePorLocalizacaoEAN:", err);
		throw new Error(err?.message || "Erro ao buscar produto na localização.");
	}
}


/**
 * @deprecated Encerra a localização sem gravar histórico. Substituído por
 * `registrarConferenciaLocalizacao`, que faz as duas coisas na mesma transação
 * (#1421). Mantido porque o endpoint segue exposto para o mobile.
 */
export async function encerrarConferenciaLocalizacao(localizacao_id: number) {
	try {
		const { data } = await api.patch(
			`/ocorrencia/localizacao/${localizacao_id}/encerrar-conferencia`,
		);
		return data;
	} catch (err: any) {
		console.error("Erro ao encerrar conferência da localização:", err);
		throw new Error(
			err?.response?.data?.message ||
				"Erro ao encerrar conferência da localização.",
		);
	}
}


// ---------- HISTÓRICO DE CONFERÊNCIAS DE LOCALIZAÇÃO (#1421) ----------

export type ResultadoConferencia = "sem_divergencia" | "com_divergencia";


export interface ConferenciaLocalizacaoItemDTO {
	conferencia_localizacao_item_id?: number;
	produto_id?: number | null;
	produto_estoque_id?: number | null;
	sku?: string | null;
	descricao?: string | null;
	quantidade_sistema: number;
	quantidade_fisica: number;
	diferenca: number;
	/** Falso = a tela carregou a linha e ninguém contou. */
	bipado: boolean;
}


export interface ConferenciaLocalizacaoDTO {
	conferencia_localizacao_id: number;
	localizacao_nome?: string | null;
	armazem_nome?: string | null;
	localizacao?: {
		localizacao_id: number;
		nome?: string;
		ean?: string;
	} | null;
	conferente_usuario?: {
		usuario_id: number;
		usuario: string;
		responsavel: string;
	} | null;
	data_conclusao: string;
	total_produtos_localizacao: number;
	total_produtos_conferidos: number;
	qtd_divergencias: number;
	resultado: ResultadoConferencia;
	solicitacao_ajuste_id?: number | null;
	/** Preenchido quando a solicitação de ajuste não pôde ser criada. */
	observacao_falha?: string | null;
	itens?: ConferenciaLocalizacaoItemDTO[];
}


export interface ContagemConferencias {
	todas: number;
	sem_divergencia: number;
	com_divergencia: number;
}


/**
 * Registra a conferência encerrada — é o endpoint único dos dois caminhos.
 * Sem divergência, o backend encerra a localização na mesma transação; com
 * divergência, ele cria a solicitação de ajuste a partir dos itens divergentes.
 *
 * Manda a lista COMPLETA de itens (inclusive os que bateram): é o histórico que
 * prova a cobertura da gaveta. `diferenca` e os totais são derivados no backend.
 */
export async function registrarConferenciaLocalizacao(payload: {
	localizacao_id: number;
	localizacao_nome?: string;
	armazem_nome?: string;
	observacoes?: string;
	itens: Array<{
		produto_id: number;
		produto_estoque_id?: number | null;
		sku?: string | null;
		descricao?: string | null;
		quantidade_sistema: number;
		quantidade_fisica: number;
		bipado: boolean;
	}>;
}): Promise<ConferenciaLocalizacaoDTO> {
	const { data } = await api.post("/conferencia-localizacao", payload);
	return data;
}


export async function listarConferenciasLocalizacao(params?: {
	search?: string;
	armazem?: string;
	dataInicio?: string;
	dataFim?: string;
	somenteDivergentes?: boolean;
	offset?: number;
	limit?: number;
}): Promise<{
	results: ConferenciaLocalizacaoDTO[];
	total: number;
	contagem: ContagemConferencias;
}> {
	const { data } = await api.get("/conferencia-localizacao", {
		params: {
			...params,
			somenteDivergentes: params?.somenteDivergentes ? "true" : undefined,
		},
	});
	return data;
}


export async function buscarConferenciaLocalizacao(
	conferencia_localizacao_id: number,
): Promise<ConferenciaLocalizacaoDTO> {
	const { data } = await api.get(
		`/conferencia-localizacao/${conferencia_localizacao_id}`,
	);
	return data;
}


export interface Armazem {
	armazem_id: number;
	nome: string;
}


export async function buscarArmazemPorEAN(
	ean: string,
): Promise<Armazem | null> {
	try {
		const res = await api.get<Armazem[]>(`/armazem`, {
			params: { ean },
		});
		return res.data.length > 0 ? res.data[0] : null;
	} catch (err) {
		console.error("Erro ao buscar armazém por EAN →", err);
		return null;
	}
}


// Usada APENAS quando um usuário é deslogado forçadamente
export async function fecharLocalizacaoEmergencia(ean: string) {
	try {
		const response = await api.get(
			`/movimentacao/fechar-localizacao-emergencia/${ean}`,
		);
		return response.data;
	} catch (error: any) {
		console.error(
			`❌ API: Erro ao fechar localização ${ean} por emergência:`,
			error,
		);
		if (error.response) {
			console.error(`❌ API: Status do erro:`, error.response.status);
			console.error(`❌ API: Dados do erro:`, error.response.data);
		}
		throw new Error(`Falha ao fechar localização ${ean} por emergência.`);
	}
}


// Criar novo produto
export async function criarProduto(dados: {
	id_tiny: number;
	sku: string;
	descricao: string;
	ean: string;
	altura: number;
	largura: number;
	comprimento: number;
	url_foto: string | null;
	marca_id?: number;
	categoria_id?: number;
}): Promise<any> {
	try {
		if (dados.url_foto === "") {
			dados.url_foto = null;
		}
		const response = await api.post("/produto", dados);
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao criar produto:",
			error.response?.data || error.message,
		);

		// Se for um erro de validação (400), retornar os detalhes específicos
		if (error.response?.status === 400 && error.response?.data) {
			const validationError = new Error(
				error.response.data.message || "Erro de validação",
			);
			(validationError as any).error = error.response.data.error;
			(validationError as any).statusCode = error.response.data.statusCode;
			(validationError as any).validationErrors =
				error.response.data.message || [];
			throw validationError;
		}

		throw new Error("Falha ao criar produto.");
	}
}


// Buscar produto por ID
export async function buscarProdutoPorId(id: number): Promise<Produto> {
	try {
		const response = await api.get(`/produto/${id}`);
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao buscar produto:",
			error.response?.data || error.message,
		);
		throw new Error("Falha ao buscar produto.");
	}
}


// Atualizar produto
export async function atualizarProduto(
	id: number,
	dados: {
		id_tiny?: number;
		sku?: string;
		descricao?: string;
		ean?: string;
		altura?: number;
		largura?: number;
		comprimento?: number;
		url_foto?: string | null;
		marca_id?: number;
		categoria_id?: number;
	},
): Promise<any> {
	try {
		if (dados.url_foto === "") {
			dados.url_foto = null;
		}
		const response = await api.patch(`/produto/${id}`, dados);
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao atualizar produto:",
			error.response?.data || error.message,
		);

		// Se for um erro de validação (400), retornar os detalhes específicos
		if (error.response?.status === 400 && error.response?.data) {
			const validationError = new Error(
				error.response.data.message || "Erro de validação",
			);
			(validationError as any).error = error.response.data.error;
			(validationError as any).statusCode = error.response.data.statusCode;
			(validationError as any).validationErrors =
				error.response.data.message || [];
			throw validationError;
		}

		throw new Error("Falha ao atualizar produto.");
	}
}


